import { type FormEvent, type ReactNode } from 'react'
import { useForm, Head, router } from '@inertiajs/react'
import { Trash2 } from 'lucide-react'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'

type Member = {
  id: string
  email: string
  first_name: string
  last_name: string
  title: string
  role: 'owner' | 'member'
  pending: boolean
  created_at: string
}

type Props = {
  members: Member[]
  current_member_id: string
  current_member_role: 'owner' | 'member'
}

export default function TeamIndex({ members, current_member_id: currentMemberId, current_member_role: currentMemberRole }: Props) {
  const isOwner = currentMemberRole === 'owner'
  const { data, setData, post, processing, errors, reset, transform } = useForm({
    email: '',
    first_name: '',
    last_name: '',
    title: '',
    role: 'member' as 'owner' | 'member',
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ member: formData }))
    post('/dashboard/settings/team', {
      preserveScroll: true,
      onSuccess: () => reset(),
    })
  }

  return (
    <>
      <Head title="Team" />
      <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
        <h1 className="mb-2 text-4xl font-semibold tracking-tight">Team</h1>
        <p className="mb-8 text-muted-foreground">Manage who can sign in to this organization.</p>

        {isOwner && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Invite a team member</CardTitle>
              <CardDescription>They'll receive an email to set their password and join.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name</Label>
                    <Input
                      id="first_name"
                      value={data.first_name}
                      onChange={e => setData('first_name', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name</Label>
                    <Input
                      id="last_name"
                      value={data.last_name}
                      onChange={e => setData('last_name', e.target.value)}
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={data.email}
                    onChange={e => setData('email', e.target.value)}
                    required
                  />
                  {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title / role at org</Label>
                    <Input
                      id="title"
                      value={data.title}
                      onChange={e => setData('title', e.target.value)}
                      placeholder="e.g. Event Coordinator"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Permission</Label>
                    <Select value={data.role} onValueChange={(v) => setData('role', v as 'owner' | 'member')}>
                      <SelectTrigger id="role">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectGroup>
                          <SelectItem value="member">Member</SelectItem>
                          <SelectItem value="owner">Owner</SelectItem>
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={processing}>Send invitation</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Members</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="text-sm">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Permission</TableHead>
                    <TableHead className="w-10 sr-only">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map(m => (
                    <MemberRow
                      key={m.id}
                      member={m}
                      isOwner={isOwner}
                      isSelf={m.id === currentMemberId}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

function MemberRow({ member, isOwner, isSelf }: { member: Member, isOwner: boolean, isSelf: boolean }) {
  function changeRole(newRole: 'owner' | 'member') {
    if (newRole === member.role) return
    router.patch(`/dashboard/settings/team/${member.id}`, {
      member: { role: newRole },
    }, { preserveScroll: true })
  }

  function remove() {
    if (!confirm(`Remove ${member.first_name} ${member.last_name}?`)) return
    router.delete(`/dashboard/settings/team/${member.id}`, { preserveScroll: true })
  }

  const fullName = `${member.first_name} ${member.last_name}`.trim()

  return (
    <TableRow>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <span>{fullName}</span>
          {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}
          {member.pending && <Badge variant="outline" className="text-xs">Pending</Badge>}
        </div>
      </TableCell>
      <TableCell className="max-w-[220px] truncate text-muted-foreground" title={member.email}>{member.email}</TableCell>
      <TableCell>{member.title}</TableCell>
      <TableCell>
        {isOwner && !isSelf ? (
          <Select value={member.role} onValueChange={(v) => changeRole(v as 'owner' | 'member')}>
            <SelectTrigger className="h-8 w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="owner">Owner</SelectItem>
              </SelectGroup>
            </SelectContent>
          </Select>
        ) : (
          <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
            {member.role === 'owner' ? 'Owner' : 'Member'}
          </Badge>
        )}
      </TableCell>
      <TableCell className="w-10 p-0 pr-2 text-right">
        {isOwner && !isSelf && (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={remove}
            aria-label={`Remove ${fullName}`}
          >
            <Trash2 className="size-4" />
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}

TeamIndex.layout = (page: ReactNode) => <DashboardLayout>{page}</DashboardLayout>
